/**
 * Legal Production KPIs and the weekly report queue (KAN-51 / KAN-76 / KAN-77 /
 * KAN-78 / KAN-79 / KAN-80).
 *
 * Three ideas shape this screen, and each of them is about a figure being
 * arguable rather than merely correct.
 *
 * **Every rate shows its components.** A percentage on its own cannot be
 * challenged by the person it describes, and a figure nobody can challenge is
 * one nobody trusts. So realization reads "75% — $75,000 billed of $100,000
 * worked", and utilization reads in hours.
 *
 * **An absence is not a zero.** An attorney with no capacity configured has no
 * utilization rate, a month nobody has computed is partial rather than flat,
 * and both say so in words.
 *
 * **Nothing on this page sends anything.** The draft queue is a review surface;
 * the send is one deliberate action behind its own grant, and the state of
 * every draft says plainly whether it has gone out.
 */
import { Fragment, lazy, Suspense, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  REPORT_DRAFT_STATE_LABELS,
  formatBps,
  type AttorneyKpi,
  type KpiPeriod,
  type KpiTrend,
  type ReportDraft,
  type ReportDraftState,
} from '@accounting/shared'
import { apiFetch } from '../lib/api'
import { useSession } from '../lib/session'
import { formatDate, formatDateTime, formatHours, formatMoney } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { PageHeader } from '../ui/PageHeader'
import { Stat, StatRow } from '../ui/Stat'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'

/** Lazily loaded for the reason every chart here is: Recharts is not free. */
const ProductivityTrend = lazy(() => import('./ProductivityTrend'))

const DRAFT_TONES: Record<ReportDraftState, Tone> = {
  draft: 'neutral',
  in_review: 'accent',
  approved: 'brand',
  sent: 'positive',
  discarded: 'neutral',
}

export function Productivity() {
  const { may } = useSession()
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7))

  const period = useQuery({
    queryKey: ['productivity', month],
    queryFn: () => apiFetch<KpiPeriod>(`/productivity?month=${month}-01`),
  })

  const trend = useQuery({
    queryKey: ['productivity', 'trend', month],
    queryFn: () => apiFetch<KpiTrend>(`/productivity/trend?months=12&month=${month}-01`),
  })

  return (
    <>
      <PageHeader
        title="Legal production"
        description="Billed, collections, realization and utilization by attorney. Every formula here is the one written down in docs/kpi-definitions.md — read it before disputing a figure."
        actions={
          <input
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            className="rounded-md border border-ink-300 px-3 py-1.5 text-sm"
            aria-label="Month"
          />
        }
      />

      {period.isPending && <p className="text-sm text-ink-600">Loading…</p>}

      {period.isError && (
        <Alert tone="error" title="The KPIs could not be loaded">
          {(period.error as Error).message}
        </Alert>
      )}

      {period.data && (
        <div className="space-y-6">
          <Summary period={period.data} />

          <Card>
            <CardHeader
              title="Twelve-month trend"
              description="Months with no computed data are marked partial rather than drawn as zero — a flat line through a month nobody has computed reads as a collapse that never happened."
            />
            <CardBody>
              <Suspense fallback={<p className="text-sm text-ink-600">Loading the chart…</p>}>
                {trend.data && <ProductivityTrend trend={trend.data} />}
              </Suspense>
            </CardBody>
          </Card>

          <Attorneys attorneys={period.data.attorneys} />
          <Drafts canEdit={may('reports.edit_draft')} canSend={may('reports.distribute')} />
        </div>
      )}
    </>
  )
}

function Summary({ period }: { period: KpiPeriod }) {
  const { summary } = period

  return (
    <>
      {summary.isPartial && (
        <Alert tone="info" title="This month is still open">
          The figures cover it up to the last sync. They are marked partial everywhere they appear,
          so a trend line does not show a dip that is really an incomplete month.
        </Alert>
      )}

      <StatRow>
        <Stat
          label="Billed"
          value={formatMoney(summary.billedMinor)}
          hint={
            summary.unattributedEntryCount > 0
              ? `${summary.unattributedEntryCount} entries worth ${formatMoney(summary.unattributedBilledMinor)} have no attorney and are in nobody’s figure.`
              : 'Value of billed time worked this month, by the attorney who logged it.'
          }
        />
        <Stat
          label="Collections"
          value={formatMoney(summary.collectionsMinor)}
          hint="Cash received against invoices, credited to the responsible attorney."
        />
        <Stat
          label="Trust transfers"
          value={formatMoney(summary.trustTransfersMinor)}
          tone="muted"
          hint="Reported apart from collections and never added to them — summing the two double-counts every matter the client funded first."
        />
        <Stat
          label="Realization"
          value={summary.realization.bps === null ? '—' : formatBps(summary.realization.bps)}
          hint={`${formatMoney(summary.realization.numerator)} billed of ${formatMoney(summary.realization.denominator)} worked.`}
        />
      </StatRow>

      {summary.attorneysWithoutCapacity > 0 && (
        <Alert tone="info" title="Utilization does not cover everybody">
          {summary.attorneysWithoutCapacity} attorney
          {summary.attorneysWithoutCapacity === 1 ? ' has' : 's have'} no monthly capacity
          configured, so {summary.attorneysWithoutCapacity === 1 ? 'their' : 'their'} utilization is
          shown as unknown rather than measured against an assumed target.
        </Alert>
      )}
    </>
  )
}

function Attorneys({ attorneys }: { attorneys: AttorneyKpi[] }) {
  return (
    <Card>
      <CardHeader
        title="By attorney"
        description="Every rate shows the two figures behind it. A percentage on its own cannot be argued with, and a figure nobody can argue with is one nobody trusts."
      />
      <TableWrap>
        <thead>
          <tr>
            <Th>Attorney</Th>
            <Th className="text-right">Billed</Th>
            <Th className="text-right">Collections</Th>
            <Th className="text-right">Trust transfers</Th>
            <Th>Realization</Th>
            <Th>Utilization</Th>
            <Th>vs. target</Th>
          </tr>
        </thead>
        <tbody>
          {attorneys.length === 0 && (
            <EmptyRow colSpan={7}>
              Nothing computed for this month yet. KPIs are recomputed after each Clio sync.
            </EmptyRow>
          )}
          {attorneys.map((row) => (
            <tr key={row.attorneyId}>
              <Td>
                {row.attorneyName}
                {row.unvaluedEntryCount > 0 && (
                  <p className="mt-0.5 text-xs text-ink-500">
                    {row.unvaluedEntryCount} entr{row.unvaluedEntryCount === 1 ? 'y has' : 'ies have'}{' '}
                    no rate or total, and {row.unvaluedEntryCount === 1 ? 'is' : 'are'} in no money
                    figure.
                  </p>
                )}
              </Td>
              <Td className="text-right tabular-nums">{formatMoney(row.billedMinor)}</Td>
              <Td className="text-right tabular-nums">{formatMoney(row.collectionsMinor)}</Td>
              <Td className="text-right tabular-nums">{formatMoney(row.trustTransfersMinor)}</Td>
              <Td>
                {row.realization.bps === null ? (
                  <span className="text-ink-500">No billable work</span>
                ) : (
                  <>
                    <span className="font-medium tabular-nums">
                      {formatBps(row.realization.bps)}
                    </span>
                    <p className="mt-0.5 text-xs text-ink-500">
                      {formatMoney(row.realization.numerator)} of{' '}
                      {formatMoney(row.realization.denominator)}
                    </p>
                  </>
                )}
              </Td>
              <Td>
                {row.utilization.bps === null ? (
                  <span className="text-ink-500">No target set</span>
                ) : (
                  <>
                    <span className="font-medium tabular-nums">
                      {formatBps(row.utilization.bps)}
                    </span>
                    <p className="mt-0.5 text-xs text-ink-500">
                      {formatHours(row.billableSeconds)} of {formatHours(row.capacitySeconds)}
                    </p>
                  </>
                )}
              </Td>
              <Td>
                {row.productivityVarianceSeconds === null ? (
                  <span className="text-ink-500">—</span>
                ) : (
                  <Badge tone={row.productivityVarianceSeconds >= 0 ? 'positive' : 'warning'}>
                    {row.productivityVarianceSeconds >= 0 ? '+' : ''}
                    {formatHours(Math.abs(row.productivityVarianceSeconds))}
                  </Badge>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
    </Card>
  )
}

/* ------------------------------------------------------------------------ *
 * The weekly report queue
 * ------------------------------------------------------------------------ */

function Drafts({ canEdit, canSend }: { canEdit: boolean; canSend: boolean }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)

  const drafts = useQuery({
    queryKey: ['productivity', 'drafts'],
    queryFn: () => apiFetch<ReportDraft[]>('/productivity/drafts'),
  })

  const move = useMutation({
    mutationFn: ({ id, state }: { id: string; state: ReportDraftState }) =>
      apiFetch(`/productivity/drafts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ state }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['productivity', 'drafts'] }),
  })

  const dispatch = useMutation({
    mutationFn: (id: string) => apiFetch(`/productivity/drafts/${id}/send`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['productivity', 'drafts'] }),
  })

  return (
    <Card>
      <CardHeader
        title="Weekly report queue"
        description="Reports are generated automatically on Monday morning. Nothing is ever sent without somebody approving it first."
      />

      <TableWrap>
        <thead>
          <tr>
            <Th>Week</Th>
            <Th>State</Th>
            <Th>Recipients</Th>
            <Th>Approved by</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {drafts.isPending && <EmptyRow colSpan={5}>Loading…</EmptyRow>}
          {drafts.data?.length === 0 && (
            <EmptyRow colSpan={5}>
              No drafts. A generated report becomes a draft when somebody addresses it.
            </EmptyRow>
          )}
          {drafts.data?.map((draft) => (
            /** Keyed on the fragment: a draft renders two sibling rows. */
            <Fragment key={draft.id}>
              <tr>
                <Td>
                  {formatDate(draft.weekStart)} – {formatDate(draft.weekEnd)}
                  <p className="mt-0.5 text-xs text-ink-500">{draft.subject}</p>
                </Td>
                <Td>
                  <Badge tone={DRAFT_TONES[draft.state]}>
                    {REPORT_DRAFT_STATE_LABELS[draft.state]}
                  </Badge>
                  {draft.sentAt && (
                    <p className="mt-0.5 text-xs text-ink-500">{formatDateTime(draft.sentAt)}</p>
                  )}
                </Td>
                <Td>
                  {/**
                    * The actual addresses, always. "12 recipients" is how the
                    * wrong list gets approved.
                    */}
                  <ul className="space-y-0.5 text-xs text-ink-700">
                    {draft.recipients.map((recipient) => (
                      <li key={recipient}>{recipient}</li>
                    ))}
                  </ul>
                </Td>
                <Td>
                  {draft.approvedByName ?? <span className="text-ink-500">Not approved</span>}
                  {draft.approvedAt && (
                    <p className="mt-0.5 text-xs text-ink-500">{formatDateTime(draft.approvedAt)}</p>
                  )}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setOpen(open === draft.id ? null : draft.id)}>
                      {open === draft.id ? 'Hide' : 'Review'}
                    </Button>

                    {canEdit && draft.state === 'draft' && (
                      <Button
                        size="sm"
                        onClick={() => move.mutate({ id: draft.id, state: 'in_review' })}
                      >
                        Send to review
                      </Button>
                    )}
                    {canEdit && draft.state === 'in_review' && (
                      <Button
                        size="sm"
                        onClick={() => move.mutate({ id: draft.id, state: 'approved' })}
                      >
                        Approve
                      </Button>
                    )}
                    {canSend && draft.state === 'approved' && (
                      <Button
                        size="sm"
                        variant="accent"
                        disabled={dispatch.isPending}
                        onClick={() => dispatch.mutate(draft.id)}
                      >
                        Send now
                      </Button>
                    )}
                    {canEdit && draft.state !== 'sent' && draft.state !== 'discarded' && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => move.mutate({ id: draft.id, state: 'discarded' })}
                      >
                        Discard
                      </Button>
                    )}
                  </div>
                </Td>
              </tr>

              {open === draft.id && (
                <tr>
                  <Td className="bg-ink-50/60" colSpan={5}>
                    {draft.state !== 'sent' && (
                      <p className="mb-2 text-xs font-semibold tracking-wide text-accent-700 uppercase">
                        Draft — not yet sent
                      </p>
                    )}
                    <pre className="max-h-96 overflow-auto rounded-md border border-ink-200 bg-white p-3 text-xs whitespace-pre-wrap text-ink-800">
                      {draft.editedBody ?? draft.generatedBody}
                    </pre>
                    {draft.editedBody !== null && (
                      <p className="mt-2 text-xs text-ink-500">
                        Edited by hand. The generated original is retained.
                      </p>
                    )}
                    {draft.delivery && (
                      <ul className="mt-3 space-y-1 text-xs">
                        {draft.delivery.map((entry) => (
                          <li key={entry.recipient} className={entry.ok ? 'text-emerald-700' : 'text-red-700'}>
                            {entry.recipient}: {entry.ok ? 'delivered' : entry.error}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </TableWrap>
    </Card>
  )
}
